import type { JSX } from 'react';
import React, { useState } from 'react';

export function Title({ text }: { text: string }): JSX.Element {
    return <h4 className="response-title">{text}</h4>;
}

export function Description({ text }: { text: string }): JSX.Element {
    return <p className='response-text'>{text}</p>;
}

export function Link({ href, children = null }: { href: string; children?: React.ReactNode }): JSX.Element {
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="response-link">
            {children || href}
        </a>
    );
}

const formatDateTime = (dateTimeString: string): string => {
    const date = new Date(dateTimeString);
    return date.toLocaleString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

// tool-specific renderers
const renderWebSearch = (data: any): JSX.Element => (
    <div className="response-item">
        <Title text={data?.title} />
        {data?.href && <Link href={data.href} />}
        {data?.body && <Description text={data.body} />}
    </div>
);

function MapSearchRenderer({ data }: { data: any }): JSX.Element {
    const [isHoursVisible, setIsHoursVisible] = useState(false);

    const formatTime = (time: string): string => {
        const [hours, minutes] = time.split(":").map(Number);
        const period = hours >= 12 ? "PM" : "AM";
        const formattedHours = hours % 12 || 12;
        return `${formattedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
    };

    const formatHours = (hours: string): string => {
        const [start, end] = hours.split("–");
        return `${formatTime(start)} - ${formatTime(end)}`;
    };

    const renderHours = (hours: Record<string, string>) => (
        <div className={`response-hours ${isHoursVisible ? "visible" : "hidden"}`}>
            {Object.entries(hours).map(([day, time]) => (
                <p key={day}>
                    {day}: {time === "00:00:00–00:00:00" ? "Closed" : formatHours(time)}
                </p>
            ))}
        </div>
    );

    const toggleHoursVisibility = () => {
        setIsHoursVisible((prev) => !prev);
    };

    if (typeof data === "string") {
        return (
            <div className="response-item">
                <p>{data}</p>
            </div>
        );
    }

    return (
        <div className="response-item">
            <Title text={data?.title} />
            {data?.url && <Link href={data.url}>{data.url}</Link>}
            {data?.address && <Description text={data.address} />}
            <p>
                <strong>Category:</strong> {data?.category || "N/A"}
            </p>
            {data.phone && (
                <p>
                    <strong>Phone:</strong> {data?.phone}
                </p>
            )}
            {data.hours && (
                <>
                    <button
                        type="button"
                        onClick={toggleHoursVisibility}
                        className="toggle-hours-button"
                        aria-label={isHoursVisible ? "Hide Hours" : "Show Hours"}
                    >
                        {isHoursVisible ? "Hide Hours" : "Show Hours"}
                    </button>
                    <p>{isHoursVisible && renderHours(data?.hours)}</p>
                </>
            )}
        </div>
    );
}

const renderMapSearch = (data: any): JSX.Element => <MapSearchRenderer data={data} />;

const renderNewsSearch = (data: any): JSX.Element => {
    return (
        <div className="response-item">
            <Title text={data.title} />
            {data.date && (
                <p className='response-text'>
                    Published on: {formatDateTime(data.date)}
                </p>
            )}
            {data.url && <Link href={data.url} />}
            {data.body && <Description text={data.body} />}
            {data.image && (
                <img
                    src={data.image}
                    alt={data.title}
                    style={{width: '50%',borderRadius: '4px', margin: '8px 0'}}
                />
            )}
        </div>
    );
};


const renderMediaSearch = (data: any): JSX.Element => {
    const modifyIframeSrc = (src: string): string => {
        if (src.includes('youtube.com') || src.includes('youtu.be') || src.includes('vimeo.com')) {
            return `${src}?autoplay=0`;
        }
        return src;
    };

    return (
        <div className="response-item">
            <Title text={data?.title} />
            {data?.content && <Link href={data.content}>{data.content}</Link>}
            {data?.description && <Description text={data.description} />}
            {data?.embed_url && (
                <iframe
                    src={modifyIframeSrc(data.embed_url)}
                    title={data.title}
                    width="100%"
                    height="300"
                    frameBorder="0"
                    className="response-iframe"
                 />
            )}
            {data?.published && data?.statistics?.viewCount && (
                <p>
                    <strong>Uploaded: </strong> {new Date(data.published).toLocaleDateString()} | 
                    <strong> Views: </strong> {data.statistics.viewCount.toLocaleString()}
                </p>
            )}
        </div>
    );
};

const renderImageSearch = (data: any): JSX.Element => {
    const { title, image, url } = data;

    return (
        <div className="response-item">
            <Title text={title} />
            {image && (
                <div>
                    <img
                        src={image}
                        alt={title}
                        style={{width: '50%', borderRadius: '4px', margin: '8px 0'}}
                    />
                </div>
            )}
            {url && (
                <div>
                    <Link href={url}/>
                </div>
            )}
        </div>
    );
};

const renderGmail = (data: any): JSX.Element => (
    <div className='response-item'>
        <Description text={data || 'Your message was sent successfully'}/>
    </div>
);

const renderGetEventsForDay = (data: any): JSX.Element => {
    const lines = typeof data === 'string' ? data.split('\n') : [];
    const events = lines.filter(line => line.startsWith('Event:'));

    return (
        <div className="response-item">
            {events.map((eventLine, _index) => {
                const [eventPart, startPart] = eventLine.replace('Event: ', '').split(', Start: ');
                const eventName = eventPart.trim();
                const startDateTime = startPart ? startPart.trim() : '';

                let formattedDate = '';
                let formattedTime = '';

                if (startDateTime) {
                    const date = new Date(startDateTime);
                    formattedDate = date.toLocaleDateString(undefined, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    });
                    formattedTime = date.toLocaleTimeString(undefined, {
                        hour: 'numeric',
                        minute: '2-digit',
                    });
                }

                return (
                    <div key={eventName + startDateTime} className='response-event-for-day'>
                        <p> <strong>{eventName}</strong></p>
                        <div className="response-text">
                            {formattedDate && (
                                <p>Date: {formattedDate}</p>
                            )}
                            {formattedTime && (
                                <p>Start Time: {formattedTime}</p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const renderAddCalendarEvent = (data: any): JSX.Element => {
    const parts = data.split(' ');
    let eventLink = parts[2];
    eventLink = eventLink.replace(/^"|"$/g, '');

    if (eventLink && !/^https?:\/\//i.test(eventLink)) {
        eventLink = `https://${eventLink}`;
    }

    return (
        <div className="response-item">
            <Description text="Your event has been created successfuly." />
            {eventLink && (
                <p>
                    <Link href={eventLink}>Click here to view your event</Link>
                </p>
            )}
        </div>
    );
};

const renderDeleteCalendarEvent = (data: any): JSX.Element => {
    const regex = /Event\s+["']?(.*?)["']?\s+at\s+(.*?)\s+on\s+(.*?)\s+has\s+been\s+deleted\.?/i;
    const match = data.match(regex);

    if (match) {
        const eventName = match[1];
        const eventTime = match[2];
        const eventDate = match[3];
        const dateTimeString = `${eventDate} ${eventTime}`;
        const formattedDateTime = formatDateTime(dateTimeString);

        return (
            <div className="response-item">
                <Description text={`Event '${eventName}' scheduled for ${formattedDateTime} has been deleted.`} />
            </div>
        );
    } 
        return (
            <div className="response-item">
                <Description text={data} />
            </div>
        );
    
};

const renderSendSlackMessage = (data: any): JSX.Element => (
    <div className='response-item'>
        <Description text={data}/>
    </div>
);

// tool renderers map
export const ToolRenderers: { [key: string]: (data: any) => JSX.Element } = {
    'search': renderWebSearch,
    'map_search': renderMapSearch,
    'news_search': renderNewsSearch,
    'media_search': renderMediaSearch,
    'image_search': renderImageSearch,
    'send_email': renderGmail,
    'get_events_for_day': renderGetEventsForDay,
    'add_calendar_event': renderAddCalendarEvent,
    'delete_calendar_event': renderDeleteCalendarEvent,
    'send_slack_message': renderSendSlackMessage
};